import round from 'lodash/round'
import type { GeoJsonProperties, Position } from 'geojson'
import { metersToYards } from '@/components/shared/x-distance.vue'
import Color from '@/lib/color'
import type { MarkupColor, ShapeFillColor } from '@/lib/markup-colors'
import {
  MARKUP_COLOR_DB_VALUE_MAP,
  MARKUP_COLOR_MAP,
  SHAPE_FILL_COLOR_DB_VALUE_MAP,
  SHAPE_FILL_COLOR_MAP,
} from '@/lib/markup-colors'
import type { StrokeStyle, StrokeWeight } from '@/lib/markup-styles'
import { STROKE_STYLE_MAP, STROKE_WEIGHT_MAP } from '@/lib/markup-styles'

import { MARKUP_PERMISSIONS } from '@/services/markup-service/constants'
import { getItem } from '@/plugins/storage'
import { ROUTE_PERMISSIONS } from '@/services/route-service-constants'

export interface MarkupOwner {
  firstName?: string
  first_name?: string
  lastName?: string
  last_name?: string
}

interface MarkupStyling {
  color: string
  weight: string
  style: string
  fillColor: string
  circleColor: string
  dbValue: string
}

export interface MarkupState {
  type: string
  isCreate?: boolean
  color?: string
  inProgressColor?: string
  weight?: string
  inProgressWeight?: string
  style?: string
  inProgressStyle?: string
  fillColor?: string
  inProgressFillColor?: string
  isFreeHand?: boolean
  properties?: GeoJsonProperties
}

/** Class for common functions that we can use across all of the markups (points, tracks, lines, etc.) */
class Markup {
  /**
   * Makes sure the markup coordinates are within a valid range
   *
   * @param coordinates - array of coordinates for a given markup
   * @return whether all the coordinates are valid or not
   */
  validateCoordinates(coordinates: Position[]): boolean {
    if (!coordinates) {
      return false
    }

    for (const coordinate of coordinates) {
      const lon = parseFloat(String(coordinate[0]))
      const lat = parseFloat(String(coordinate[1]))

      if (isNaN(lon) || isNaN(lat)) {
        return false
      }

      if (lon < -180 || lon > 180) {
        return false
      }

      if (lat < -90 || lat > 90) {
        return false
      }
    }

    return true
  }

  /**
   * Returns color definition object by key (e.g., 'orange'). This returns standard markup colors used for waypoints,
   * lines, and shape strokes. For shape fills, see getFillColor. Optionally falls back to the default markup color for
   * the current app if useDefault is set to true.
   *
   * @param key - Color key
   * @param useDefault - If true, fall back to default color for the current app if lookup fails.
   * @return Color definition (see lib/markup-colors.ts)
   */
  getColor(key: string, useDefault = false): MarkupColor | undefined {
    const color = MARKUP_COLOR_MAP.get(key)
    if (!color && useDefault) {
      return this.getDefaultColor('fish')
    }
    return color
  }

  /**
   * Returns color definition object by its DB value (e.g., "8,122,255,1", same as returned by Color.toString). This
   * returns standard markup colors used for waypoints, lines, and shape strokes. Optionally falls back to the default
   * markup color for the current app if useDefault is set to true.
   *
   * @param dbValue - Color DB value (as returned by Color.toString)
   * @param useDefault - If true, fall back to default color for the current app if lookup fails.
   * @return Color definition (see lib/markup-colors.ts)
   */
  getColorByDbValue(
    dbValue: string,
    useDefault?: boolean
  ): MarkupColor | undefined {
    const color = MARKUP_COLOR_DB_VALUE_MAP.get(dbValue)
    if (!color && useDefault) {
      return this.getDefaultColor('fish')
    }
    return color
  }

  /**
   * Returns fill color definition object by key (e.g., 'blue'). Optionally falls back to the default markup color for
   * the current app if useDefault is set to true.
   *
   * @param key - Color key
   * @param useDefault - If true, fall back to default color for the current app if lookup fails.
   * @return Fill color definition (see lib/markup-colors.ts)
   */
  getFillColor(
    key: string,
    useDefault = false
  ): ShapeFillColor | MarkupColor | undefined {
    const color = SHAPE_FILL_COLOR_MAP.get(key)
    if (!color && useDefault) {
      return this.getDefaultColor('fish')
    }
    return color
  }

  /**
   * Returns fill color definition object by its DB value (e.g., "8,122,255,1", same as returned by Color.toString).
   * Optionally falls back to the default fill color for the current app if useDefault is set to true.
   *
   * @param dbValue - Color DB value (as returned by Color.toString)
   * @param useDefault - If true, fall back to default fill color for the current app if lookup fails.
   * @return Fill color definition (see lib/markup-colors.ts)
   */
  getFillColorByDbValue(
    dbValue: string,
    useDefault?: boolean
  ): ShapeFillColor | undefined {
    const fillColor = SHAPE_FILL_COLOR_DB_VALUE_MAP.get(dbValue)
    if (!fillColor && useDefault) {
      return this.getDefaultFillColor()
    }
    return fillColor
  }

  /**
   * Returns the default style options for each markup type
   *
   * @param type - markup type
   * @return default styling object
   */
  getDefaultStyling(type: string): MarkupStyling {
    const defaultColor = this.getDefaultColor('fish')
    const defaultStyling: MarkupStyling = {
      color: '',
      weight: '',
      style: '',
      fillColor: '',
      circleColor: '',
      dbValue: '',
    }

    switch (type) {
      case 'line':
        defaultStyling.color = this.getColor('orange')?.rgba ?? ''
        defaultStyling.weight = this.getWeight('normal')?.key ?? ''
        defaultStyling.style = this.getStroke('solid')?.key ?? ''
        break
      case 'track':
        defaultStyling.color = this.getColor('cyan')?.rgba ?? ''
        defaultStyling.weight = this.getWeight('normal')?.key ?? ''
        defaultStyling.style = this.getStroke('dash')?.key ?? ''
        break
      case 'shape':
        defaultStyling.color = this.getColor('orange')?.rgba ?? ''
        defaultStyling.weight = this.getWeight('normal')?.key ?? ''
        defaultStyling.style = this.getStroke('solid')?.key ?? ''
        defaultStyling.fillColor =
          (this.getFillColor('blue') as ShapeFillColor | undefined)?.fillRgba ??
          ''
        break
      case 'waypoint':
        defaultStyling.color = defaultColor?.rgba ?? ''
        defaultStyling.circleColor = defaultColor?.circleColorRgba ?? ''
        defaultStyling.dbValue = defaultColor?.dbValue ?? ''
        break
    }

    return defaultStyling
  }

  /**
   * A simple alternative to the full getWaypointColors function. Only returns
   * the color name. Works for waypoints, lines, tracks, and shapes.
   *
   * @param colorArray - Markup color array to get a name for
   * @return Color name
   */
  getColorName(colorArray?: number[] | null): string | undefined {
    const defaultColor = 'green'

    // if no color set, show the default
    if (!colorArray || colorArray.length === 0) {
      return defaultColor
    }

    const dbValue = colorArray.map((c) => parseInt(String(c), 10)).join(',')
    return MARKUP_COLOR_DB_VALUE_MAP.get(dbValue)?.key ?? defaultColor
  }

  /**
   * Generate the corresponding icon string for an svg icon
   *
   * @param icon - Loaded svg icon
   * @return Formatted svg icon string
   */
  svgIconString(icon: string): string {
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(icon)
  }

  /**
   * Returns the associated stroke style definition object for a given key.
   *
   * @param style - Style key (e.g., 'solid' or 'dash')
   * @param useDefault - If true, fallback to the default style if lookup fails.
   * @returns Stroke style definition object (see lib/markup-styles.ts)
   */
  getStroke(style: string, useDefault = false): StrokeStyle | null | undefined {
    return (
      STROKE_STYLE_MAP.get(style) ??
      (useDefault ? STROKE_STYLE_MAP.get('solid') : null)
    )
  }

  /**
   * Returns the associated stroke weight definition object for a given key.
   *
   * @param weight - Weight key (e.g., 'thin' or 'normal')
   * @param useDefault - If true, fallback to the default weight if lookup fails.
   * @returns Stroke weight definition object (see lib/markup-styles.ts)
   */
  getWeight(
    weight: string,
    useDefault = false
  ): StrokeWeight | null | undefined {
    return (
      STROKE_WEIGHT_MAP.get(weight) ??
      (useDefault ? STROKE_WEIGHT_MAP.get('normal') : null)
    )
  }

  /**
   * Set the styling for markups on load or initial create
   *
   * @param markup - markup object to load or create
   */
  setStyles(markup: MarkupState): void {
    const defaultStyling = this.getDefaultStyling(markup.type)

    // Use the default styles while drawing a new markup
    if (markup.isCreate) {
      markup.color = defaultStyling.color
      markup.inProgressColor = markup.color
      markup.weight = defaultStyling.weight
      markup.inProgressWeight = markup.weight
      markup.style = defaultStyling.style
      markup.inProgressStyle = markup.style
      markup.fillColor = defaultStyling.fillColor
      markup.inProgressFillColor = markup.fillColor
    }
    // Use the styles saved to the markup if they exist
    else {
      // Color
      markup.color = defaultStyling.color

      if (markup.properties && markup.properties.color) {
        try {
          markup.color = Color.fromRgbaArray(
            markup.properties.color as number[]
          ).toRgbaString()
        } catch (_) {
          markup.color = defaultStyling.color
        }
      }

      // Style
      markup.style = defaultStyling.style

      if (markup.properties && markup.properties.style) {
        markup.style =
          this.getStroke(markup.properties.style as string, true)?.key ?? ''
      }

      // Weight
      if (markup.properties && markup.properties.weight) {
        markup.weight =
          this.getWeight(markup.properties.weight as string, true)?.key ?? ''
      } else {
        markup.weight = defaultStyling.weight
      }

      // Fill color
      markup.fillColor = defaultStyling.fillColor

      if (markup.properties && markup.properties.fill_color) {
        try {
          markup.fillColor = Color.fromRgbaArray(
            markup.properties.fill_color as number[]
          ).toRgbaString()
        } catch (_) {
          markup.fillColor = defaultStyling.fillColor
        }
      }
    }
  }

  /**
   * Save the markup styles to the geo_json properties
   *
   * @param markup - markup being edited
   */
  saveStylesToGeoJson(markup: MarkupState): void {
    if (!markup.properties) {
      markup.properties = {}
    }

    if (
      !markup.inProgressColor ||
      !markup.inProgressStyle ||
      !markup.inProgressWeight
    ) {
      return
    }

    markup.color = markup.inProgressColor
    markup.style = markup.inProgressStyle
    markup.weight = markup.inProgressWeight

    // Save the active color
    const rgbaValues = markup.color
      .substring(5, markup.color.length - 1)
      .split(', ')
    markup.properties['color'] = rgbaValues.map(Number)

    // Save the active style
    markup.properties.style = markup.style

    // Save the active weight
    markup.properties.weight = markup.weight

    // Save the active fill color for shapes
    if (markup.fillColor && markup.inProgressFillColor) {
      markup.fillColor = markup.inProgressFillColor
      const rgbaFillValues = markup.fillColor
        .substring(5, markup.fillColor.length - 1)
        .split(', ')
      markup.properties['fill_color'] = rgbaFillValues.map(Number)
    }

    // Save if it is freehand
    if (markup.type === 'line' || markup.type === 'shape') {
      markup.properties['free_hand'] = markup.isFreeHand
    }
  }

  /**
   * Returns the proper breakdown of distance depending on length and preference
   *
   * @param meters - distance in meters of the markup
   * @param unitType - type of measurement
   * @return Distance with the proper unit of measure
   */
  distanceFormatter(
    meters: number,
    unitType?: string
  ): { distance: number | string; UOM: string } {
    let units = unitType
    if (!unitType) {
      units = getItem('unitType') as string | undefined
    }
    let distance: number | string = ''
    let UOM = ''

    if (units === 'imperial') {
      const yards = metersToYards(meters)

      // show feet for < 100yds for offroad and backcountry
      if (yards < 100) {
        distance = round(yards * 3)
        UOM = 'ft'
      } else if (yards < 1000) {
        distance = round(yards, 1)
        UOM = 'yds'
      } else {
        distance = round(yards / 1760, 2)
        UOM = 'mi'
      }
    } else if (units === 'metric') {
      if (meters < 500) {
        distance = round(meters, 1)
        UOM = 'm'
      } else {
        distance = round(meters / 1000, 2)
        UOM = 'km'
      }
    }

    return { distance, UOM }
  }

  /**
   * Returns the default markup color definition for the given app.
   * @param app - Application name (optional)
   * @return The default markup color definition (see MARKUP_COLORS in lib/markup-colors.ts for fields)
   */
  getDefaultColor(app?: string): MarkupColor | undefined {
    switch (app) {
      case 'backcountry':
        return MARKUP_COLOR_MAP.get('blue')
      case 'offroad':
        return MARKUP_COLOR_MAP.get('black')
      case 'fish':
        return MARKUP_COLOR_MAP.get('green')
      default:
        return MARKUP_COLOR_MAP.get('orange')
    }
  }

  /**
   * Returns the default waypoint color definition for the current app.
   * @return The default markup color definition
   */
  getWaypointDefaultColor(): MarkupColor | undefined {
    return this.getDefaultColor('fish')
  }

  /**
   * Returns the default line color definition.
   * @return The default markup color definition
   */
  getLineDefaultColor(): MarkupColor | undefined {
    return MARKUP_COLOR_MAP.get('orange')
  }

  /**
   * Returns the default shape stroke color definition.
   * @return The default markup color definition
   */
  getShapeDefaultColor(): MarkupColor | undefined {
    return MARKUP_COLOR_MAP.get('orange')
  }

  /**
   * Returns the default route color definition.
   * @return The default markup color definition
   */
  getRouteDefaultColor(): MarkupColor | undefined {
    return MARKUP_COLOR_MAP.get('black')
  }

  /**
   * Returns the default fill color definition.
   * @return The default fill color definition
   */
  getDefaultFillColor(): ShapeFillColor | undefined {
    return SHAPE_FILL_COLOR_MAP.get('blue')
  }

  /**
   * Parse the owner's short name (eg. 'G. Walrus')
   *
   * @param owner - Markup owner
   * @return Short name of owner
   */
  getOwnerShortName(owner: MarkupOwner | null | undefined): string {
    if (!owner) {
      return ''
    }

    const first = owner.firstName || owner.first_name
    const last = owner.lastName || owner.last_name

    if (first && last) {
      // eg. Gordy W
      return `${first} ${last.substr(0, 1)}`
    } else if (first || last) {
      // eg. just 'Gordy' or just 'Walrus'
      return first || last || ''
    }

    // no names set or only last name set
    return 'A friend'
  }

  /**
   * Get the label for the delete primary action on the markup card
   *
   * @param isShared - Whether the markup has been shared with others
   * @return Label
   */
  getDeletePrimaryActionLabel(isShared: boolean): string {
    return isShared ? 'Delete for Everyone' : 'Delete'
  }

  /**
   * Get the label for the delete message on the markup card
   *
   * @param isShared - Whether the markup has been shared with others
   * @param markupType - Type of markup
   * @param folderName - Name of the folder feature (eg. "folder")
   * @return Label
   */
  getDeleteMessageLabel(
    isShared: boolean,
    markupType: string,
    folderName: string
  ): string {
    return isShared
      ? `Deleting this ${markupType} will delete it for anyone you've shared it with, and will also remove it from any shared ${folderName}s that it's been added to. You can't undo this.`
      : ''
  }

  /**
   * Get the label for the delete title on the markup card
   *
   * @param isShared - Whether the markup has been shared with others
   * @param markupType - Type of markup
   * @return Label
   */
  getDeleteTitleLabel(isShared: boolean, markupType: string): string {
    return isShared
      ? `This ${markupType} is shared with others. Are you sure you want to delete it?`
      : `Are you sure you want to delete this ${markupType}?`
  }

  /**
   * Check if a markup can be copied based on its shared status and permissions
   * @param isShared
   * @param permissions
   * @returns Whether the markup can be copied
   */
  canBeCopied(isShared: boolean, permissions: string[]): boolean {
    return !!(
      isShared &&
      permissions &&
      (permissions.includes(MARKUP_PERMISSIONS.COPY) ||
        permissions.includes(ROUTE_PERMISSIONS.COPY))
    )
  }
}

const markup = new Markup()
export default markup
